import PropTypes from 'prop-types';

import { Link } from 'react-router-dom';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import Typography from 'Components/Typography';

import ConsultationCTA from 'Containers/ConsultationCTA';

import company from 'assets/content/company';
import content from 'assets/content/footer';

const shadow = '0 10px 14px -10px rgba(0, 0, 0, 0.04)';

// Properties shared bewteen <a> and <Link> tags
const LinkStyles = css`
  margin: 8px 0;
  font-size: 14px;
  line-height: 1.5em;
  letter-spacing: 0.5px;
`;

const StyledFooter = styled.footer`
  ${({ theme, paddedOnRightOnDesktop }) => `
    display: flex;
    flex-direction: row;
    width: 100%;
    color: ${theme.palette.common.white.light};
    background: ${theme.palette.common.noir.dark};
    box-shadow: ${shadow};
    ${theme.breakpoints.match('md')} {
      flex-direction: column;
    }
    ${
      paddedOnRightOnDesktop &&
      `
        padding-right: 450px;
        ${theme.breakpoints.match('md')} {
          padding-right: 0;
        }
      `
    }
  `}
`;

const CTA = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  order: 2;
  justify-content: center;
  align-items: center;
  min-height: 224px;
  color: ${({ theme }) => theme.palette.common.white.light};
  background: ${({ theme }) => theme.palette.common.noir.dark2};
  ${({ theme }) => theme.breakpoints.match('md')} {
    order: 1;
  }
`;

const Grid = styled.div`
  ${({ theme }) => `
    flex: 2 1 0%;
    order: 1;
    display: grid;
    padding:  ${theme.props.verticalSpacingDes}px 0px ${theme.props.verticalSpacingDes}px 50px;
    place-items: flex-start start;
    justify-content: space-around;
    justify-items: start;
    grid-template-areas:
      "contactUs      gift       privacy        bCorp"
      "careers        stylists   terms          bCorp"
      "blog           partner    accessibility  bCorp"
      "faq            .           .             bCorp"
      "social         social      social        bCorp"
      "copywright     copywright  supplements   supplements"
      "sitemap        sitemap     supplements   supplements"
      "donotsell      donotsell   supplements   supplements";
    ${theme.breakpoints.match('md')} {
      order: 2;
      padding: 48px ${theme.props.verticalSpacingMob}px 70px;
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-areas:
        "contactUs      careers     gift"
        "blog           faq         stylists"
        "partner        privacy     terms"
        "accessibility  .           ."
        "border         border      border"
        "bCorp          bCorp       bCorp"
        "social         social      social"
        "copywright     copywright  copywright"
        "sitemap        sitemap     sitemap"
        "donotsell      donotsell   donotsell"
        "supplements    supplements supplements";
      justify-items: center;
      text-align: center;
      flex: 1;
      & a {
        margin-bottom: 30px;
      };
      & div a {
        margin-bottom: 0;
      };
    };
    ${theme.breakpoints.match('sm')} {
      padding: 80px 24px 62px;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "contactUs      careers"
        "gift           blog"
        "faq            stylists"
        "privacy        partner"
        "terms          accessibility"
        "border         border"
        "bCorp          bCorp"
        "social         social"
        "copywright     copywright"
        "sitemap        sitemap"
        "donotsell      donotsell"
        "supplements    supplements"
    }
  `}
`;

const Nav = styled.nav`
  display: contents;
`;

const StyledLink = styled(Link)`
  ${LinkStyles}
  color: ${({ theme }) => theme.palette.common.white.light};
  ${({ theme }) => theme.breakpoints.match('md')} {
    margin: 8px;
  }
`;

const ContactUsLink = styled(StyledLink)`
  grid-area: contactUs;
`;

const CareersLink = styled(StyledLink)`
  grid-area: careers;
`;

const GiftLink = styled(StyledLink)`
  grid-area: gift;
`;

const BlogLink = styled.a`
  grid-area: blog;
  ${LinkStyles}
  color: ${({ theme }) => theme.palette.common.white.light};
  ${({ theme }) => theme.breakpoints.match('md')} {
    margin: 8px;
  }
`;

const FaqLink = styled(StyledLink)`
  grid-area: faq;
`;

const Social = styled.div`
  grid-area: social;
`;

const TermsLink = styled(StyledLink)`
  grid-area: terms;
`;

const Privacy = styled.div`
  grid-area: privacy;
  display: flex;
  flex-direction: column;
  align-items: start;
  > a {
    margin-bottom: 0;
  }
  ${({ theme }) => theme.breakpoints.match('md')} {
    align-items: center;
  }
`;

const PartnerProgramLink = styled.a`
  ${LinkStyles};
  ${({ theme }) => `
    grid-area: partner;
    color: ${theme.palette.common.white.light};
    ${theme.breakpoints.match('md')} {
      margin: ${theme.spacing.s8}px;
    }
  `}
`;

const StylistsLink = styled(StyledLink)`
  grid-area: stylists;
`;
const AccessibilityLink = styled(StyledLink)`
  grid-area: accessibility;
`;
const Border = styled.div`
  display: none;
  grid-area: border;
  margin: ${({ theme }) => theme.spacing.s16}px 0;
  width: 100%;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  ${({ theme }) => theme.breakpoints.match('md')} {
    display: block;
  }
`;

const LogoWrapper = styled.div`
  grid-area: bCorp;
  ${props => props.theme.breakpoints.match('md')} {
    margin: ${({ theme }) => theme.spacing.s24}px;
  }
`;

const Copyright = styled(Typography)`
  ${({ theme }) => `
    grid-area: copywright;
    margin-bottom: ${theme.spacing.s8}px;
    color: ${theme.palette.common.grey.medium};
  `}
`;

const Sitemap = styled(Typography)`
  ${({ theme }) => `
    grid-area: sitemap;
    margin-bottom: ${theme.spacing.s8}px;
    color: ${theme.palette.common.grey.medium};
  `}
`;

const Donotsell = styled.a`
  ${({ theme }) => `
    grid-area: donotsell;
    color: ${theme.palette.common.grey.medium};
    ${LinkStyles}
    ${theme.breakpoints.up('sm')} {
      margin-bottom: ${theme.spacing.s36}px;
    }
    ${theme.breakpoints.up('md')} {
      margin-bottom: 0px;
    }
  `}
`;

const SocialImg = styled.img`
  ${({ theme }) => `
    margin: ${theme.spacing.s72}px ${theme.spacing.s32}px ${theme.spacing.s16}px 0px;
    width: 20px;
    height: 20px;
    ${theme.breakpoints.match('md')} {
      margin: ${theme.spacing.s16}px;
    }
  `}
`;

const Supplements = styled(Typography)`
  ${({ theme }) => `
    grid-area: supplements;
    max-width: 400px;
    margin: 0 34px ${theme.spacing.s40}px 34px;
    padding: 20px 18px;
    border: 1px solid ${theme.palette.common.noir.medium};
    text-align: left;
    line-height: 1.5;
    letter-spacing: 0.5px;
    font-size: 12px;
    color: ${theme.palette.common.white.light};
    ${theme.breakpoints.up('sm')} {
      margin-bottom: 24px;
      padding: 21px ${theme.spacing.s24}px;
    }
    ${theme.breakpoints.up('md')} {
      margin-bottom: 0;
      padding: 15px 20px;
    }
    ${theme.breakpoints.match('md')} {
      text-align: center;
      max-width: 344px;
      margin: 0 16px ${theme.spacing.s40}px 16px;
      padding: 20px 14px;
    }
  `}
`;

const Logo = styled.img`
  height: 115px;
  margin: 0px 15px;
`;

const currentYear = new Date().getFullYear();

export const Footer = ({ hideCTA, paddedOnRightOnDesktop }) => {
  return (
    <StyledFooter data-testid="footer" id="footer" paddedOnRightOnDesktop={paddedOnRightOnDesktop}>
      {!hideCTA && (
        <CTA data-testid="footerCta">
          <Typography align="center" paragraph variant="h3">
            {content.message}
          </Typography>
          <ConsultationCTA dataFrom="footer" theme="whiteBg" />
        </CTA>
      )}
      <Grid>
        <Nav>
          <ContactUsLink data-click="contact-us" data-from="footer" to="/contact">
            Contact us
          </ContactUsLink>
          <CareersLink data-click="careers" data-from="footer" to="/careers">
            Careers
          </CareersLink>
          <GiftLink data-click="gift" data-from="footer" to="/gift">
            Gift Prose
          </GiftLink>
          <BlogLink data-click="blog" data-from="footer" href="/blog/">
            Blog
          </BlogLink>
          <FaqLink data-click="faq" data-from="footer" to="/faq">
            Help + FAQ
          </FaqLink>
          <StylistsLink
            data-click="for-stylists"
            data-from="footer"
            to="/pages/stylist-content-call"
          >
            For stylists
          </StylistsLink>
          <TermsLink data-click="terms" data-from="footer" to="/terms">
            Terms of use
          </TermsLink>

          <Privacy>
            <StyledLink data-click="privacy" data-from="footer" to="/privacy">
              Privacy policy
            </StyledLink>
            <Typography color="sorbet" variant="mono4">
              updated
            </Typography>
          </Privacy>
          <PartnerProgramLink
            data-click="partner-program"
            data-from="footer"
            href="https://app.impact.com/campaign-campaign-info-v2/Prose.brand "
            rel="noopener noreferrer"
            target="_blank"
          >
            Partner program
          </PartnerProgramLink>
          <AccessibilityLink data-click="accessibility" data-from="footer" to="/accessibility">
            Accessibility
          </AccessibilityLink>
        </Nav>
        <Social>
          {company.social.map(social => (
            <a
              key={social.name}
              data-click={social.name}
              data-from="footer"
              href={social.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <SocialImg alt={`${social.name} icon - vector`} src={social.img} />
            </a>
          ))}
        </Social>
        <Border />
        <LogoWrapper>
          <Link data-click="bcorp" data-from="footer" to={content.bCorp.link}>
            <Logo alt={`${content.bCorp.alt} logo - vector`} src={content.bCorp.image} />
          </Link>
          <Logo alt={`${content.climateNeutral.alt} logo`} src={content.climateNeutral.image.src} />
        </LogoWrapper>
        <Copyright variant="mono3">
          &copy; {currentYear} {company.legalName}
        </Copyright>
        <Sitemap
          data-click="sitemap"
          data-from="footer"
          markupName={Link}
          to="/sitemap"
          variant="mono3"
        >
          Site map
        </Sitemap>
        <Donotsell href={content.doNotSell.link}>
          <Typography variant="mono3">{content.doNotSell.copy}</Typography>
        </Donotsell>{' '}
        <Supplements markupName="p" variant="p3">
          *These statements have not been evaluated by the Food and Drug Administration. This
          product is not intended to diagnose, treat, cure or prevent any disease.
        </Supplements>
      </Grid>
    </StyledFooter>
  );
};

Footer.defaultProps = {
  hideCTA: false,
  paddedOnRightOnDesktop: false,
};

Footer.propTypes = {
  hideCTA: PropTypes.bool,
  paddedOnRightOnDesktop: PropTypes.bool,
};

export default Footer;
